<template>
  <div class="character-display" @click="onTap" ref="characterDisplay">
    <div class="character-container">
      <div class="coin-container" ref="coinContainer">
        <img :src="coinImage" alt="Coin" class="clickable-coin" />
      </div>
      <!-- Flying coins container -->
      <div class="flying-coins-container">
        <img 
          v-for="coin in flyingCoins" 
          :key="coin.id"
          :src="coin.image" 
          :alt="'Flying coin'"
          class="flying-coin" 
          :style="{
            '--start-x': coin.startX + 'px',
            '--start-y': coin.startY + 'px',
            '--end-x': coin.endX + 'px',
            '--end-y': coin.endY + 'px'
          }"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';

export default {
  name: 'CharacterDisplay',
  props: {
    currentCoinImage: {
      type: String,
      default: ''
    }
  },
  emits: ['tap'],
  setup(props, { emit }) {
    // Используем computed для получения изображения монеты, чтобы сохранить реактивность
    const coinImage = computed(() => props.currentCoinImage || new URL('../assets/one.png', import.meta.url).href);
    
    // Flying coins state
    const flyingCoins = ref([]);
    let coinId = 0;
    
    // Import small coin image
    const smallCoinImage = new URL('../assets/one.png', import.meta.url).href;
    
    const onTap = (event) => {
      // Get the coin container element
      const coinContainer = event.currentTarget.querySelector('.coin-container');
      
      // Apply slight depression effect (coin bends slightly downward)
      coinContainer.style.transform = 'translateY(2px) scale(0.99)';
      
      // Reset after animation
      setTimeout(() => {
        coinContainer.style.transform = '';
      }, 100);
      
      // Create flying coin from click position
      createFlyingCoin(event, props.currentCoinImage);
      
      emit('tap');
    };
    
    const createFlyingCoin = (event, currentCoinImage) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const id = ++coinId;
      
      // Start position exactly where clicked
      const clickX = event.clientX - rect.left - rect.width / 2;
      const clickY = event.clientY - rect.top - rect.height / 2;
      
      // End position near balance (top of screen)
      const endX = (Math.random() - 0.5) * 100;
      const endY = -window.innerHeight + 100;
      
      const coinData = {
        id,
        startX: clickX,
        startY: clickY,
        endX,
        endY,
        image: currentCoinImage || smallCoinImage
      };
      
      flyingCoins.value = [...flyingCoins.value, coinData];
      
      // Remove coin after animation (slower speed = longer duration)
      setTimeout(() => {
        flyingCoins.value = flyingCoins.value.filter(coin => coin.id !== id);
      }, 2500); // Slower animation - 2.5 seconds
    };

    return {
      coinImage,
      smallCoinImage,
      flyingCoins,
      onTap
    };
  }
};
</script>

<style scoped>
.character-display {
  display: flex;
  justify-content: center;
  margin: 40px 0 30px 0; /* Поднял монету ещё выше (с 60px до 40px - так как отрицательное значение поднимает выше) */
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
}

.character-container {
  position: relative;
}

.coin-container {
  width: 306px; /* Увеличенный размер на 15px от предыдущего (291px + 15px) */
  height: 306px; /* Увеличенный размер на 15px от предыдущего (291px + 15px) */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.1s ease;
  position: relative;
  transform-origin: center center;
}

.clickable-coin {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 0 15px rgba(82, 199, 255, 0.5));
  transition: transform 0.15s ease;
}

.flying-coins-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.flying-coin {
  position: absolute;
  width: 28px;
  height: 28px;
  object-fit: contain;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  opacity: 0;
  animation: flyToBalance 2.5s forwards; /* Slower animation */
  animation-delay: var(--delay, 0ms);
}

@keyframes flyToBalance {
  0% {
    transform: translate(var(--start-x, -50%), var(--start-y, -50%));
    opacity: 1;
    scale: 1;
  }
  60% {
    opacity: 1;
    scale: 1.2;
  }
  80% {
    opacity: 0.7;
    scale: 0.8;
  }
  100% {
    transform: translate(var(--end-x, -50%), var(--end-y, -50%));
    opacity: 0;
    scale: 0.3;
  }
}

/* Responsive styles */
@media (max-width: 480px) {
  .coin-container {
    width: 283px; /* 268px + 15px */
    height: 283px; /* 268px + 15px */
  }
  
  .character-display {
    margin: 80px 0 25px 0; /* Adjusted for smaller screens with proper spacing */
  }
}

@media (max-width: 320px) {
  .coin-container {
    width: 260px; /* 245px + 15px */
    height: 260px; /* 245px + 15px */
  }
  
  .character-display {
    margin: 70px 0 20px 0; /* Even more compact for smallest screens */
  }
}
</style>